<template>
  <div
    data-availability-block
    class="h-10 bg-secondary flex absolute rounded z-10 flex items-center justify-center"
    :style="{
      width: blockSize + 'px'
    }"
  >
    <div
      class="handle--left h-full rounded-l pin-l bg-primary absolute z-10"
      @mousedown="selectHandle"
      :style="{
        cursor: 'col-resize',
        width: blockSize / 2 + 'px'
      }"
    />
    <div class="w-full flex items-center relative">
      <template v-if="elRect && elRect.width > labelWidthThreshold">
        <div
          class="w-full text-sm select-none truncate text-center"
        >{{ timeString }}</div>
        <span
          title="Repeating time block"
          class="w-5 h-5 rounded text-sm flex items-center justify-center bg-primary text-secondary absolute pin-r mr-4"
          v-if="localTime.type === types.TIME_TYPE_REPEATED"
        >
          R
        </span>
      </template>
    </div>
    <div
      class="handle--right h-full rounded-r pin-r bg-primary absolute z-10"
      @mousedown="selectHandle"
      :style="{
        cursor: 'col-resize',
        width: blockSize / 2 + 'px'
      }"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'AvailabilityBlock',

  props: ['time', 'blockSize', 'offset', 'weekOffset', 'calendarType'],

  created () {
    window.addEventListener('mouseup', this.resetHandle)
    window.addEventListener('mousemove', this.resize)
  },

  beforeDestory () {
    window.removeEventListener('mouseup', this.resetHandle)
    window.removeEventListener('mousemove', this.resize)
  },

  mounted () {
    this.setUI()

    this.setRects()

    if (!this.time.id) {
      let handle = this.$el.querySelector('.handle--right')
      this.selectHandle({
        target: handle,
        pageX: handle.getBoundingClientRect().x
      })
    }
  },

  data () {
    return {
      handle: null,
      elRect: {},
      parentRect: {},
      mouseX: 0,
      possibleCollisions: null,
      localTime: this.time,
      types: {
        TIME_TYPE_UNSET: 0,
        TIME_TYPE_SINGLE: 1,
        TIME_TYPE_REPEATED: 2
      },
      labelWidthThreshold: 130
    }
  },

  computed: {
    timeString () {
      return moment()
        .set('hour', this.localTime.start.hour)
        .set('minute', this.localTime.start.minute)
        .set('second', 0)
        .format('h:mmA') +
        ' - ' +
      moment()
        .set('hour', this.localTime.end.hour)
        .set('minute', this.localTime.end.minute)
        .set('second', 0)
        .format('h:mmA')
    }
  },

  methods: {
    setUI () {
      let style = this.convertTimeToUIStyle(this.localTime)
      this.$el.style.left = style.left + 'px'
      this.$el.style.width = style.width + 'px'
    },

    setRects () {
      this.elRect = this.$el.getBoundingClientRect()
      this.parentRect = this.$el.parentNode.getBoundingClientRect()
    },

    selectHandle (e) {
      this.handle = e.target
      this.mouseX = e.pageX
      this.setRects()
    },

    resetHandle () {
      if (this.handle) { // dont trigger all blocks
        this.setRects()
        this.updateTime()
      }

      this.handle = null
      this.mouseX = 0
      this.possibleCollisions = null
    },

    collides (e) {
      // memoisation to gain a little bit of performance.
      if (this.possibleCollisions === null) {
        let left = this.parentRect.width + this.parentRect.left
        let right = 0

        let times = Array.call([], ...this.$el.parentNode.querySelectorAll('[data-availability-block]'))

        times.filter(el => {
          return el !== this.$el
        }).map(el => {
          return el.getBoundingClientRect()
        }).forEach(rect => {
          if (rect.left > this.mouseX && rect.left <= left) {
            left = rect.left
          }

          if (rect.right < this.mouseX && rect.right >= right) {
            right = rect.right
          }
        })

        this.possibleCollisions = { left, right }
      }

      return (e.pageX + this.offset) >= this.possibleCollisions.left ||
        (e.pageX - this.offset) <= this.possibleCollisions.right
    },

    resize (e) {
      if (!this.handle || this.collides(e)) return

      let leftX = this.elRect.left - this.parentRect.left

      if (this.handle.classList.contains('handle--right')) {
        this.$el.style.width = this.clamp(
          this.snapToGrid(e.pageX + this.offset - this.elRect.left),
          0,
          this.parentRect.width - leftX) - this.offset + 'px'
      } else {
        let snap = this.snapToGrid(e.pageX - this.mouseX)
        let left = this.clamp(leftX + snap, -1, this.parentRect.width)
        let width = this.clamp(this.elRect.width - snap, 0, this.parentRect.width)
        if (left < 0 || width === 0) return

        this.$el.style.left = left + 'px'
        this.$el.style.width = width + 'px'
      }
    },

    snapToGrid (value) {
      return Math.round(value / (this.blockSize + this.offset)) * (this.blockSize + this.offset)
    },

    clamp (value, min, max) {
      return Math.min(Math.max(value, min), max)
    },

    norm (val, min, max) {
      return (val - min) / (max - min)
    },

    lerp (norm, min, max) {
      return (max - min) * norm + min
    },

    convertTimeToUIStyle (time) {
      let start = this.lerp(
        this.norm(
          this.clamp(time.start.hour + (time.start.minute / 60), 8, 20),
          8, 20
        ),
        0, 48
      )

      let end = this.lerp(
        this.norm(
          this.clamp(time.end.hour + (time.end.minute / 60), 8, 20),
          8.25, 20.25
        ),
        0, 48
      ) - start

      return {
        left: start * (this.blockSize + this.offset),
        width: this.blockSize + end * (this.blockSize + this.offset)
      }
    },

    convertUIStyleToTime () {
      let startNorm
      let start = this.lerp(
        startNorm = this.norm(
          (this.elRect.left - this.parentRect.left) / (this.blockSize + this.offset),
          0, 48
        ),
        8, 20
      )

      let end = this.lerp(
        startNorm + this.norm(
          (this.elRect.width - this.blockSize) / (this.blockSize + this.offset),
          0, 48
        ),
        8.25, 20.25
      )

      return {
        start: {
          hour: Math.floor(start),
          minute: start * 60 % 60
        },
        end: {
          hour: Math.floor(end),
          minute: end * 60 % 60
        }
      }
    },

    updateTime () {
      this.localTime = {
        id: this.time.id,
        weekday: this.time.weekday,
        type: this.time.type,
        ...this.convertUIStyleToTime()
      }

      let block = {
        offset: this.weekOffset,
        type: this.calendarType,
        time: this.localTime
      }

      this.updateAvailabilityTime(block)
    },

    ...mapActions(['updateAvailabilityTime'])
  },
  watch: {
    time () {
      this.localTime = this.time
      this.setUI()
    }
  }
}
</script>
